import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { fmtCurrency, fmtPercent, fmtNumber } from "../lib/format";
import { TopBar } from "../components/TopBar";
import { formatPeriodRange } from "../components/PeriodBadge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";

const money = (v) => (
  <span className={cn("font-mono tabular-nums", v < 0 ? "text-red-600" : "text-foreground")}>{fmtCurrency(v)}</span>
);

const ArchivedTrafficTable = ({ rows, withB, testId }) => (
  <div className="w-full overflow-x-auto rounded-md border border-border">
    <Table data-testid={testId} className={withB ? "min-w-[1000px]" : "min-w-[900px]"}>
      <TableHeader>
        <TableRow className="bg-muted/50">
          <TableHead>Geo</TableHead>
          <TableHead className="text-right">Total leads</TableHead>
          <TableHead className="text-right">Valid leads</TableHead>
          <TableHead className="text-right">CRG</TableHead>
          <TableHead className="text-right">CPA</TableHead>
          <TableHead className="text-right">Gu. FTD's</TableHead>
          <TableHead className="text-right">FTD's</TableHead>
          <TableHead className="text-right">Expense</TableHead>
          {withB && <TableHead>B</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={withB ? 9 : 8} className="py-6 text-center text-sm text-muted-foreground">
              No traffic rows in this period.
            </TableCell>
          </TableRow>
        ) : (
          rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell className="font-medium">{r.geo || "\u2014"}</TableCell>
              <TableCell className="text-right font-mono tabular-nums">{fmtNumber(r.total_leads)}</TableCell>
              <TableCell className="text-right font-mono tabular-nums">{fmtNumber(r.valid_leads)}</TableCell>
              <TableCell className="text-right font-mono tabular-nums">{fmtPercent(r.crg)}</TableCell>
              <TableCell className="text-right font-mono tabular-nums">{fmtCurrency(r.cpa)}</TableCell>
              <TableCell className="text-right font-mono tabular-nums">{fmtNumber(r.gu_ftd)}</TableCell>
              <TableCell className="text-right font-mono tabular-nums">{fmtNumber(r.ftd)}</TableCell>
              <TableCell className={cn("text-right font-mono tabular-nums", r.expense < 0 && "text-red-600")}>
                {fmtCurrency(r.expense)}
              </TableCell>
              {withB && (
                <TableCell>
                  <Badge variant="outline" className="font-mono">{r.b}</Badge>
                </TableCell>
              )}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </div>
);

const BalanceSummary = ({ title, opening, payments, expenses, total, paymentEntries }) => (
  <div className="rounded-md border border-border">
    <div className="border-b border-border bg-muted/50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {title}
    </div>
    <div className="divide-y divide-border text-sm">
      <div className="flex items-center justify-between px-3 py-2"><span>Opening balance</span>{money(opening)}</div>
      <div className="flex items-center justify-between px-3 py-2"><span>Payments</span>{money(payments)}</div>
      <div className="flex items-center justify-between px-3 py-2"><span>Expenses</span>{money(expenses)}</div>
      <div className="flex items-center justify-between bg-accent/30 px-3 py-2 font-semibold"><span>Total</span>{money(total)}</div>
    </div>
    {paymentEntries && paymentEntries.length > 0 && (
      <div className="border-t border-border px-3 py-2">
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Payment log</p>
        {paymentEntries.map((e) => (
          <div key={e.id} className="flex items-center justify-between gap-2 py-0.5 text-xs">
            <span className="truncate text-muted-foreground">
              {e.date}
              {e.note ? ` — ${e.note}` : ""}
            </span>
            {money(e.amount)}
          </div>
        ))}
      </div>
    )}
  </div>
);

export default function HistoryPeriodPage() {
  const { periodId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/history/periods/${periodId}`);
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load archived week");
    } finally {
      setLoading(false);
    }
  }, [periodId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30">
        <TopBar crumbs={[{ label: "Weekly History", to: "/history" }]} />
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-muted/30">
        <TopBar crumbs={[{ label: "Weekly History", to: "/history" }]} />
        <div className="mx-auto max-w-7xl px-4 py-16 text-center text-muted-foreground">Archived week not found.</div>
      </div>
    );
  }

  const { period, brand_weeks, source_weeks } = data;

  return (
    <div className="min-h-screen bg-muted/30">
      <TopBar crumbs={[{ label: "Weekly History", to: "/history" }, { label: "Archived Week" }]} />
      <main className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 data-testid="history-period-heading" className="text-2xl font-semibold tracking-tight sm:text-3xl">
              {formatPeriodRange(period.week_start, period.week_end)}
            </h1>
            <Badge variant="secondary" className="gap-1 font-normal">
              <Lock className="h-3 w-3" /> Read-only
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Closed {period.closed_by === "manual" ? "by manual reset" : "automatically on Tuesday rollover"}.
          </p>
        </div>

        <section className="space-y-4">
          <h2 className="text-lg font-semibold tracking-tight">Brands</h2>
          {brand_weeks.length === 0 ? (
            <p className="text-sm text-muted-foreground">No brands existed in this period.</p>
          ) : (
            brand_weeks.map((bw, idx) => (
              <Card key={bw.id} className="shadow-sm" data-testid={`history-brand-card-${idx}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold">{bw.brand_name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                    <BalanceSummary
                      title="Balance"
                      opening={bw.opening_balance}
                      payments={bw.payments}
                      expenses={bw.expenses}
                      total={bw.total}
                      paymentEntries={bw.payment_entries}
                    />
                  </div>
                  <ArchivedTrafficTable rows={bw.rows} withB={false} testId={`history-brand-rows-${idx}`} />
                </CardContent>
              </Card>
            ))
          )}
        </section>

        <section className="space-y-4">
          <h2 className="text-lg font-semibold tracking-tight">Sources</h2>
          {source_weeks.length === 0 ? (
            <p className="text-sm text-muted-foreground">No sources existed in this period.</p>
          ) : (
            source_weeks.map((sw, idx) => (
              <Card key={sw.id} className="shadow-sm" data-testid={`history-source-card-${idx}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold">{sw.source_name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                    <BalanceSummary
                      title="PH Balance"
                      opening={sw.ph_opening}
                      payments={sw.ph_payments}
                      expenses={sw.ph_expenses}
                      total={sw.ph_total}
                      paymentEntries={sw.ph_payment_entries}
                    />
                    <BalanceSummary
                      title="QG Balance"
                      opening={sw.qg_opening}
                      payments={sw.qg_payments}
                      expenses={sw.qg_expenses}
                      total={sw.qg_total}
                      paymentEntries={sw.qg_payment_entries}
                    />
                  </div>
                  <ArchivedTrafficTable rows={sw.rows} withB testId={`history-source-rows-${idx}`} />
                </CardContent>
              </Card>
            ))
          )}
        </section>
      </main>
    </div>
  );
}
